import { useSWRConfig } from 'swr';
import { emptyObject } from '@constants/empty';
import useInfiniteFetch from '@hooks/useInfiniteFetch';
import processCarouselEntity from './utils/processCarouselEntity';
import processCourseEntity from './utils/processCourseEntity';
import processTestSeriesEntity from './utils/processTestSeriesEntity';
import processSubjectsEntity from './utils/processSubjectsEntity';
import processEducatorsEntity from './utils/processEducatorsEntity';
import processDefaultEntity from './utils/processDefaultEntity';
import processTopologyEntity from './utils/processTopologyEntity';
import {
  isCarouselEntityType,
  isCourseEntityType,
  isTestSeriesEntityType,
  isSubjectEntityType,
  isEducatorEntityType,
  isTopologyEntityType,
  isCuratedPlaylistEntityType
} from './utils/getBlockType';
import processCuratedPlaylist from './utils/processCuratedPlaylist';

const isReachingEnd = (data) => {
  if (data === undefined) {
    return false;
  }

  return !data.next || data?.results?.length >= data?.count;
};

const feedrowMiddleware = (useSWRNext) => (getKey, fetcher, config) => {
  const swr = useSWRNext(getKey, fetcher, config);

  return {
    ...swr,
    data:
      swr.data?.map((page) => {
        if (page.processed) {
          return page;
        }

        return {
          ...page,
          processed: true,
          results:
            page.results?.map((feedBlock) => {
              const feedEntityType = feedBlock.extraBlockInfo.entityType;

              if (isCarouselEntityType(feedEntityType)) {
                return processCarouselEntity(feedBlock);
              }

              if (isCourseEntityType(feedEntityType)) {
                return processCourseEntity(feedBlock);
              }

              if (isTestSeriesEntityType(feedEntityType)) {
                return processTestSeriesEntity(feedBlock);
              }

              if (isSubjectEntityType(feedEntityType)) {
                return processSubjectsEntity(feedBlock);
              }

              if (isEducatorEntityType(feedEntityType)) {
                return processEducatorsEntity(feedBlock);
              }

              if (isTopologyEntityType(feedEntityType)) {
                return processTopologyEntity(feedBlock);
              }
              if (isCuratedPlaylistEntityType(feedEntityType)) {
                return processCuratedPlaylist(feedBlock);
              }
              return processDefaultEntity(feedBlock);
            }) || []
        };
      }) || []
  };
};

const useInfiniteFeedRow = ({ initialURL, swrOptions = emptyObject }) => {
  const { cache } = useSWRConfig();
  const { data, isLoading, isValidating, setSize, ...rest } = useInfiniteFetch(
    initialURL,
    { applyOffsetsAutomatically: false },
    {
      use: [feedrowMiddleware],
      revalidateOnMount: true,
      revalidateFirstPage: true,
      ...swrOptions
    }
  );

  const hasNextPage = !isReachingEnd(data);

  const fetchMoreData = () => {
    if (hasNextPage) {
      setSize((currentPageIndex) => {
        return currentPageIndex + 1;
      });
    }
  };

  const refetch = () => {
    cache.clear();
    setSize(1);
  };

  return {
    data,
    isLoading,
    fetchMoreData,
    isValidating,
    refetch,
    hasNextPage,
    ...rest
  };
};

export default useInfiniteFeedRow;
